import { IconButton, Modal, Switch } from "@carbon6/c6-frontend-design";
import { useId, useState, type ReactNode } from "react";
import {
  isScheduleNotFoundError,
  isScheduleWriteForbiddenError,
  updateTrainingSchedule,
  type TrainingScheduleOperationsItem,
} from "@/api/trainingSchedule";
import type { UserRole } from "@/app";
import { resolveLoadErrorMessage } from "@/components/dataset/loadState";
import { HatModalHeader, HatTextModal } from "@/components/modal";
import { HatTextTooltip } from "@/components/tooltip";
import { createLogger } from "@/logging";
import { useGpuCapacityTotal } from "@/components/gpu";
import { notifyScheduleChanged } from "./scheduleBus";
import { EditFields } from "./ScheduleEditFields";
import { LastObservations, ScheduleFacts, StatusBand } from "./ScheduleReadFacts";
import { buildSchedulePatch, editDraftFromItem, type ScheduleEditDraft } from "./scheduleEdit";
import { canCommandSchedule } from "./scheduleFormat";
import type { TrainingScheduleCommand } from "./useTrainingSchedules";
import styles from "./ScheduleEditDialog.module.css";

const logger = createLogger("page/training/schedules");

type ScheduleEditDialogProps = {
  item: TrainingScheduleOperationsItem;
  role: UserRole | null;
  // 쓰기 자격(can_manage_schedules). 없으면 읽기 전용이다. 편집 폼과 명령을 표시하지 않는다.
  canManage: boolean;
  // 명령(pause/resume/삭제) 실행. 부모가 목록 컨트롤러의 run 을 감싸서 행 잠금(pendingId)을
  // 목록과 공유한다.
  onCommand: (command: TrainingScheduleCommand) => Promise<void>;
  onClose: () => void;
};

/**
 * Schedule 편집 다이얼로그. 목록 행 클릭으로 여는 페이지 로컬 모달이다(창 kind 를 늘리지 않는다).
 *
 * - 맨 위 상태 띠는 status 칩, 이름, 다음 실행, 그 status 를 켜고 끄는 활성 스위치를 한 줄에 둔다.
 * - 되돌릴 수 없는 삭제는 푸터 왼쪽 끝에 둔다. 저장·취소의 반대편이라 잘못 누르지 않는다.
 * - 그 아래는 2단이다. 왼쪽은 PATCH 로 바꿀 수 없는 사실(구성·최근 확인 결과)이고, 오른쪽은
 *   PATCH 할 수 있는 축만 가진 편집 폼이다.
 * - 실행 이력은 목록 행 클릭이 여는 전체 화면 창이 맡는다.
 * - 저장·취소는 스크롤과 무관한 푸터에 둔다.
 * - 읽기 전용(자격 없음·삭제된 행)이면 편집 단이 없어 1단이 된다.
 *
 * 낙관적 갱신은 하지 않는다. 저장이나 명령이 성공하면 scheduleBus 로 목록을 다시 받게 하고
 * 다이얼로그를 닫는다. 연 채로 갱신하면 요약과 목록이 서로 다른 버전을 표시한다.
 */
export function ScheduleEditDialog({
  item,
  role,
  canManage,
  onCommand,
  onClose,
}: ScheduleEditDialogProps): ReactNode {
  const titleId = useId();
  const [draft, setDraft] = useState<ScheduleEditDraft>(() => editDraftFromItem(item));
  const [saving, setSaving] = useState(false);
  const [busyCommand, setBusyCommand] = useState<TrainingScheduleCommand | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  function updateDraft(patch: Partial<ScheduleEditDraft>): void {
    setDraft((prev) => ({ ...prev, ...patch }));
  }

  // 삭제된 스케줄은 soft-cancel 이라 행은 남지만 되살릴 수 없다. 읽기만 가능하다.
  const editable = canManage && canCommandSchedule(item);
  const busy = saving || busyCommand !== null;
  // 여유 GPU 조건의 실제 상한. 사이드바 위젯 스냅샷을 함께 읽는다.
  const totalGpus = useGpuCapacityTotal();
  const patchPreview = buildSchedulePatch(item, draft, { totalGpus });
  const recurrenceWarning = patchPreview.kind === "patch" && patchPreview.recurrenceChanged;

  async function save(): Promise<void> {
    if (patchPreview.kind === "invalid") {
      setError(patchPreview.message);
      return;
    }
    if (patchPreview.kind === "unchanged") {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateTrainingSchedule(item.id, patchPreview.patch, role);
      notifyScheduleChanged();
      onClose();
    } catch (saveError) {
      logger.error("Failed to update the schedule.", saveError);
      setError(resolveWriteErrorMessage(saveError));
    } finally {
      setSaving(false);
    }
  }

  // 삭제하면 행이 읽기 전용이 되므로 닫는다. 일시정지와 재개는 창을 열어 둔다. 부모가 교체한 item 이
  // 스위치와 칩에 반영된다. 켜고 끌 때 창이 닫히면 조작할 때마다 다시 열어야 한다.
  async function runCommand(command: TrainingScheduleCommand): Promise<void> {
    setBusyCommand(command);
    setError(null);
    try {
      await onCommand(command);
      if (command === "cancel") {
        onClose();
      }
    } catch (commandError) {
      logger.error("Failed to run a schedule command.", commandError);
      setError(resolveWriteErrorMessage(commandError));
    } finally {
      setBusyCommand(null);
    }
  }

  return (
    <>
      <Modal
        open
        onClose={() => {
          if (!busy) {
            onClose();
          }
        }}
        size="lg"
        maxHeight="min(88vh, 52rem)"
        className={styles.dialog}
        closeOnBackdropClick={!busy}
        closeOnEscape={!busy}
        aria-labelledby={titleId}
      >
        <HatModalHeader title="Schedule 편집" titleId={titleId} close={{ disabled: busy }} />
        <Modal.Body padding={false}>
          <div className={editable ? `${styles.body} ${styles.bodyTwoPane}` : styles.body}>
            <StatusBand
              item={item}
              toggle={
                editable ? (
                  // 활성/일시정지는 되돌릴 수 있는 한 축이라 스위치 하나로 둔다. 켜면 재개, 끄면
                  // 일시정지다. 글자 없는 스위치라 접근성 이름만 준다. 다이얼로그는 낙관적 갱신을
                  // 하지 않으므로 명령이 끝날 때까지 잠근다.
                  <HatTextTooltip
                    text={
                      item.status === "paused"
                        ? "켜면 다음 실행 시각을 지금 기준으로 다시 정합니다. 멈춘 동안 놓친 실행은 몰아서 하지 않습니다."
                        : "끄면 일시정지합니다. 다음 실행부터 멈추고, 언제든 다시 켤 수 있습니다."
                    }
                  >
                    <div className={styles.activeSwitch}>
                      <Switch
                        checked={item.status !== "paused"}
                        disabled={busy}
                        aria-label="활성"
                        onChange={(event) =>
                          void runCommand(event.target.checked ? "resume" : "pause")
                        }
                      />
                    </div>
                  </HatTextTooltip>
                ) : null
              }
            />
            <div className={styles.readPane}>
              <ScheduleFacts item={item} showEditableAxes={!editable} />
              <LastObservations item={item} />
            </div>
            {editable ? (
              <div className={styles.editPane}>
                <EditFields
                  item={item}
                  draft={draft}
                  updateDraft={updateDraft}
                  recurrenceWarning={recurrenceWarning}
                  totalGpus={totalGpus}
                />
              </div>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          {/* 왼쪽: 되돌릴 수 없는 삭제와 오류·읽기 전용 안내. 오른쪽: 취소·저장. 삭제를 저장과 같은
              묶음에 두면 잘못 누르기 쉽다. 그래서 반대편 끝에 둔다. */}
          <div className={styles.footerStart}>
            {editable ? (
              <IconButton
                text="삭제"
                size="sm"
                variant="solid"
                tone="danger"
                disabled={busy}
                onClick={() => setCancelConfirmOpen(true)}
              />
            ) : null}
            {error === null ? (
              editable ? null : (
                <p className={styles.readOnlyNotice}>
                  {canManage
                    ? "삭제된 Schedule입니다. 실행 이력만 볼 수 있고 다시 켤 수 없습니다. 계속 실행하려면 새 Schedule을 만들어 주세요."
                    : "이 화면은 읽기 전용입니다. Schedule 수정은 admin 권한이 필요합니다."}
                </p>
              )
            ) : (
              <p className={styles.error} role="alert">
                {error}
              </p>
            )}
          </div>
          <div className={styles.footerActions}>
            <IconButton
              text={editable ? "취소" : "닫기"}
              size="sm"
              variant="surface"
              disabled={busy}
              onClick={onClose}
            />
            {editable ? (
              <IconButton
                text={saving ? "저장 중…" : "저장"}
                size="sm"
                variant="solid"
                disabled={busy}
                onClick={() => void save()}
              />
            ) : null}
          </div>
        </Modal.Footer>
      </Modal>

      {/* 삭제는 상류 soft-cancel 이라 행과 이력은 남지만 되살릴 수 없다. 그래서 일시정지와 달리 한 번 확인한다. */}
      <HatTextModal
        open={cancelConfirmOpen}
        label="Schedule 삭제"
        eyebrow={item.name}
        caption="삭제한 Schedule은 다시 켤 수 없습니다. 실행 이력은 목록의 전체 필터에서 계속 볼 수 있습니다. 잠시 멈추려면 활성 스위치를 꺼 주세요."
        okText="삭제"
        error
        busy={busyCommand === "cancel"}
        onOkPress={() => {
          setCancelConfirmOpen(false);
          void runCommand("cancel");
        }}
        onCancelPress={() => setCancelConfirmOpen(false)}
      />
    </>
  );
}

// 쓰기 실패 문구. 등급 게이트(403)와 스코프 밖(404)은 원인을 그대로 알리고, 나머지는 일반 문구다.
// 목록에 보이는 행이 404 를 반환하면 부재가 아니라 접근 문제다. 공용 workspace 의 Schedule 은
// research 그룹 소속에게만 열리고 admin 우회가 없다.
function resolveWriteErrorMessage(error: unknown): string {
  if (isScheduleWriteForbiddenError(error)) {
    return "Schedule 수정 권한이 없습니다. admin 권한이 필요합니다.";
  }
  if (isScheduleNotFoundError(error)) {
    return "이 Schedule의 Workspace에 닿을 수 없습니다. 운영자여도 research 그룹에 속해야 공용 Workspace의 Schedule을 고칠 수 있습니다.";
  }
  return resolveLoadErrorMessage(error);
}
